import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Inbox, Loader2, MessageSquareText, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  open: 'bg-krug-gold/15 text-krug-goldbright',
  answered: 'bg-krug-teal/15 text-krug-tealbright',
  closed: 'bg-white/10 text-slate-400',
};

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('bs-BA');
};

export const AdminTickets = ({ authHeaders, tk, onDataChange }) => {
  const [filter, setFilter] = useState('all');
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, answered: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [detail, setDetail] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const detailRef = useRef(null);
  detailRef.current = detail;

  const statusLabel = (s) =>
    s === 'answered' ? tk.statusAnswered : s === 'closed' ? tk.statusClosed : tk.statusOpen;

  const fetchDetail = useCallback(
    async (ticketId) => {
      const res = await fetch(`${API}/api/admin/app-tickets/${ticketId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('detail');
      return res.json();
    },
    [authHeaders],
  );

  const fetchList = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoading(true);
        setError(false);
      }
      try {
        const qs = filter === 'all' ? '' : `?status=${filter}`;
        const res = await fetch(`${API}/api/admin/app-tickets${qs}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('list');
        const data = await res.json();
        setTickets(data.tickets || []);
        if (data.counts) setCounts(data.counts);
        setError(false);
      } catch {
        if (!silent) setError(true);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [filter, authHeaders],
  );

  useEffect(() => {
    fetchList();
    const interval = setInterval(() => {
      fetchList(true);
      const open = detailRef.current;
      if (open) fetchDetail(open.ticket.ticket_id).then(setDetail).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchList, fetchDetail]);

  const openTicket = async (ticket) => {
    setReplyText('');
    setDetail({ ticket, messages: [] });
    try {
      const data = await fetchDetail(ticket.ticket_id);
      setDetail(data);
      fetchList(true);
      onDataChange?.();
    } catch {
      toast.error(tk.unavailable);
      setDetail(null);
    }
  };

  const sendReply = async () => {
    if (!detail || replyText.trim().length < 2) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/app-tickets/${detail.ticket.ticket_id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ message: replyText.trim() }),
      });
      if (!res.ok) throw new Error('reply');
      toast.success(tk.replySent);
      setReplyText('');
      setDetail(await fetchDetail(detail.ticket.ticket_id));
      fetchList(true);
      onDataChange?.();
    } catch {
      toast.error(tk.replyFail);
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    if (!detail) return;
    try {
      const res = await fetch(`${API}/api/admin/app-tickets/${detail.ticket.ticket_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('status');
      toast.success(tk.statusChanged);
      setDetail(await fetchDetail(detail.ticket.ticket_id));
      fetchList(true);
      onDataChange?.();
    } catch {
      toast.error(tk.statusFail);
    }
  };

  const total = (counts.open || 0) + (counts.answered || 0) + (counts.closed || 0);
  const filterButtons = [
    ['all', tk.all, total],
    ['open', tk.open, counts.open || 0],
    ['answered', tk.answered, counts.answered || 0],
    ['closed', tk.closed, counts.closed || 0],
  ];

  if (detail) {
    const t = detail.ticket;
    const status = t.status || 'open';
    return (
      <section data-testid="ticket-detail-section">
        <button
          type="button"
          data-testid="ticket-back-btn"
          onClick={() => { setDetail(null); fetchList(); }}
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-krug-tealbright transition-colors duration-300"
        >
          <ArrowLeft size={14} />
          {tk.back}
        </button>

        <div className="mt-5 rounded-2xl glass-dark p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 data-testid="ticket-detail-subject" className="font-heading text-xl md:text-2xl font-extrabold text-white">
                {t.subject}
              </h2>
              <p className="mt-1.5 text-xs text-slate-500">
                {t.user_name} ·{' '}
                <a href={`mailto:${t.user_email}`} className="text-krug-tealbright hover:underline">
                  {t.user_email}
                </a>
                {t.category_label ? ` · ${t.category_label}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                data-testid="ticket-detail-status"
                className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${STATUS_STYLE[status] || STATUS_STYLE.open}`}
              >
                {statusLabel(status)}
              </span>
              {status !== 'closed' ? (
                <button
                  type="button"
                  data-testid="ticket-close-btn"
                  onClick={() => changeStatus('closed')}
                  className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-slate-300 hover:text-white hover:border-krug-gold/50 transition-colors duration-300"
                >
                  {tk.close}
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="ticket-reopen-btn"
                  onClick={() => changeStatus('open')}
                  className="rounded-full border border-krug-tealbright/40 px-4 py-2 text-xs font-bold text-krug-tealbright hover:bg-krug-teal/10 transition-colors duration-300"
                >
                  {tk.reopen}
                </button>
              )}
            </div>
          </div>

          <div data-testid="ticket-thread" className="mt-6 space-y-3">
            {detail.messages.length === 0 && (
              <p className="py-6 text-center">
                <Loader2 size={18} className="mx-auto animate-spin text-krug-tealbright" />
              </p>
            )}
            {detail.messages.map((m) => {
              const isAdmin = m.sender === 'admin';
              return (
                <div
                  key={m.message_id}
                  data-testid={`ticket-message-${m.message_id}`}
                  className={`max-w-[85%] rounded-2xl px-5 py-4 ${
                    isAdmin
                      ? 'ml-auto bg-krug-teal/10 border border-krug-teal/20'
                      : 'bg-white/[0.04] border border-white/10'
                  }`}
                >
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    {isAdmin ? tk.support : m.sender_name || t.user_name} · {fmtDate(m.created_at)}
                  </p>
                  <p className="mt-2 text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{m.text}</p>
                </div>
              );
            })}
          </div>

          {status !== 'closed' && (
            <div data-testid="ticket-reply-form" className="mt-6 space-y-3">
              <textarea
                rows={4}
                data-testid="ticket-reply-textarea"
                placeholder={tk.replyPlaceholder}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-krug-tealbright resize-none transition-colors duration-300"
              />
              <button
                type="button"
                data-testid="ticket-reply-send"
                disabled={sending || replyText.trim().length < 2}
                onClick={sendReply}
                className="inline-flex items-center gap-2 rounded-full bg-krug-gold px-5 py-2.5 text-xs font-bold text-krug-navy hover:bg-krug-goldbright disabled:opacity-50 transition-colors duration-300"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {tk.replySend}
              </button>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section data-testid="tickets-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white flex items-center gap-3">
          <MessageSquareText size={24} className="text-krug-tealbright" />
          {tk.title}
          <span className="text-sm font-bold text-krug-goldbright">({total})</span>
        </h2>
        <button
          type="button"
          data-testid="tickets-refresh-btn"
          onClick={() => fetchList()}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-slate-300 hover:text-white hover:border-krug-gold/50 transition-colors duration-300"
        >
          <RefreshCw size={13} />
          {tk.refresh}
        </button>
      </div>

      <div data-testid="tickets-filters" className="mt-6 flex flex-wrap gap-2">
        {filterButtons.map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            data-testid={`tickets-filter-${key}`}
            onClick={() => setFilter(key)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors duration-300 ${
              filter === key ? 'bg-krug-gold text-krug-navy' : 'border border-white/15 text-slate-300 hover:text-white'
            }`}
          >
            {label} ({count})
          </button>
        ))}
      </div>

      <div data-testid="tickets-list" className="mt-6 space-y-3">
        {loading && (
          <p data-testid="tickets-loading" className="rounded-2xl glass-dark px-6 py-10 text-center text-sm text-slate-500">
            <Loader2 size={18} className="mx-auto animate-spin text-krug-tealbright" />
            <span className="mt-3 block">{tk.loading}</span>
          </p>
        )}
        {!loading && error && (
          <div data-testid="tickets-error" className="rounded-2xl glass-dark px-6 py-10 text-center">
            <Inbox size={22} className="mx-auto text-slate-600" />
            <p className="mt-3 text-sm text-slate-500">{tk.unavailable}</p>
            <button
              type="button"
              data-testid="tickets-retry-btn"
              onClick={() => fetchList()}
              className="mt-4 rounded-full bg-krug-teal px-5 py-2.5 text-xs font-bold text-white hover:bg-krug-tealbright hover:text-krug-navy transition-colors duration-300"
            >
              {tk.retry}
            </button>
          </div>
        )}
        {!loading && !error && tickets.length === 0 && (
          <p data-testid="tickets-empty" className="rounded-2xl glass-dark px-6 py-10 text-center text-sm text-slate-500">
            {tk.empty}
          </p>
        )}
        {!loading &&
          !error &&
          tickets.map((t) => (
            <button
              key={t.ticket_id}
              type="button"
              data-testid={`ticket-item-${t.ticket_id}`}
              onClick={() => openTicket(t)}
              className="w-full text-left rounded-2xl glass-dark px-5 py-4 border border-transparent hover:border-krug-tealbright/30 transition-colors duration-300"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {(t.admin_unread || 0) > 0 && (
                    <span
                      data-testid={`ticket-unread-${t.ticket_id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-400"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      {tk.newLabel}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className={`truncate text-sm ${(t.admin_unread || 0) > 0 ? 'font-extrabold text-white' : 'font-medium text-slate-300'}`}>
                      {t.subject}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500 truncate">
                      {t.user_name} · {t.user_email}
                      {t.category_label ? ` · ${t.category_label}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${STATUS_STYLE[t.status] || STATUS_STYLE.open}`}
                  >
                    {statusLabel(t.status)}
                  </span>
                  <span className="hidden sm:block text-xs text-slate-500">
                    {fmtDate(t.last_message_at || t.created_at)}
                  </span>
                </div>
              </div>
            </button>
          ))}
      </div>
    </section>
  );
};

export default AdminTickets;
